import { WizCommand, WizGroupCommand } from '@wizeoo/akm-component-lib';
import { BaselineEngine, COMPUTE_MODE, DataLoadObj, EM_ACTION, LoadDataService } from '@wizeoo/akm-compute-lib';
import { Baseline, BaselineLite, BaselineValue, BaselineValueLite, DataPack, DataPackSet, EntityModificationTrace, Vision, VisionStep, WidgetConfigurationObj } from '@wizeoo/akm-message-lib';
import { AkmBDD, BddBaseline, BddFEI, LocalFei } from '@wizeoo/akm-worker-lib';
import { from, take } from 'rxjs';
import { DataMessage } from '../../manager/baselines/data.message';
import { BASIC_ENTITY_TYPE } from '../../model/enums';
import { Model } from '../../model/model';
import { FeiVO } from '../../vo/workingdata/fei.vo';
import { DeletedEmsCmd } from '../em/deleted-ems.cmd';
import { LocalDataCommand } from '../workingdata/local-data.cmd';
import { CountDataCommand } from './count-data.cmd';
import { FindDataLinesCommand } from './find-datalines.cmd';
import { FindDataValuesCommand } from './find-datavalues.cmd';

/**
 * Service de gestion du chargement des données, en cache local ET en remote
 */
export class WidgetDataService extends LoadDataService {

  constructor(model: Model) {
    super(model);
  }
  public get model(): Model {
    return this._model as Model;
  }

  /**
   * reset du dataload avant merge avec les données du commit
   * @param dlo 
   */
  private resetDataload(dlo: DataLoadObj): void {

    dlo.remoteBaselines = {};
    dlo.remoteBaselineValues = [];

    dlo.deletedBaselines = new Map(); // EMs de suppression sur BLs
    dlo.deletedBaselineValues = new Map(); // EMs de suppression sur BLVs

    dlo.toPersistBaseline = new Map();
    dlo.toDieBaseline = new Map();

    dlo.localLoaded = false;
    dlo.nbLinesLoaded = 0;
    dlo.nbValuesLoaded = 0;
    dlo.remoteLoaded = false;
  }

  /**
   * Alimentation d'un BddFei pour persistence en cache du dataload
   * SANS les baselines, elles sont stockées à part
   * @param dlo 
   * @returns BddFEI
   */
  public dataloadToLocalFei(dlo: DataLoadObj): BddFEI {
    // clone sans les baselines
    let smallLocal: LocalFei = { revisionNumber: dlo.revisionNumber, key: dlo.uniqueKey, level: dlo.visionStep.level, scen: dlo.scenario };
    let bddFei: BddFEI = { key: dlo.uniqueKey, level: dlo.visionStep.level, scen: dlo.scenario, value: smallLocal };
    return bddFei;
  }

  /** rince les FEIs pour les widgets, sur modifications des pools de lignes (visionGenerateView) */
  public resetAll(): void {
    this.model.dataMap = new Map();
    this.model.dataEngine.crossVisions = new Map(); // on rince la map des crossVisions
    // console.log('resetAll dlo');
  }

  /** 
   * suppression d'un dataload spécifique 
   * @param level 
   * @param scenario 
   */
  public resetOne(visionCode: string, level: number, scenario: string): void {
    this.model.dataMap.get(visionCode)?.get(level)?.delete(scenario);
    if (this.model.dataMap.get(visionCode)?.get(level)?.size === 0) {
      this.model.dataMap.get(visionCode).delete(level);
    }
    // console.log('resetOne',visionCode, level, scenario, this.model.dataMap);
  }

  /* ##############################################################################
            Calcul Moteur BACK
    ############################################################################### */

  /**
   * Chargement des données de calcul du moteur BACK
   * @param fei le fei à charger
   * @param configuration: WidgetConfigurationObj
   */
  public loadComputeData(fei: FeiVO, configuration: WidgetConfigurationObj): void {

    //console.log('remote loadComputeData', fei);
    // on va alimenter un dlo vide
    // let dlo: DataLoadObj = this.getDataloadByKey(fei.visionCode, fei.visionStep.level, fei.scenario);

    // if (!dlo) { // pas encore inscrit, première demande
    //   const dlo: DataLoadObj = new DataLoadObj(this.getFeiLocalKey(fei.visionStep, fei.scenario), fei.visionStep, fei.scenario);
    //   dlo.remoteComputed = true; // on considère qu'il est OK, c'est du compute
    //   // inscription dans la map
    //   this.feedMapDataload(dlo);
    //   dlo.state === DataLoadObj.COMPLETE; // on considère qu'il est bon
    // }
    fei.computed = true; // on le marque comme calculé, c'est du compute
  }

  /* ##############################################################################
            Calcul Front: tout doit être chargé localement
    ############################################################################### */

  /**
   * #### étape 01 : demande de chargement des données par le FEI
   * 
   * @param vision
   * @param visionStep 
   * @param scenario 
   * @returns 
   */
  public loadWidgetData(vision: Vision, visionStep: VisionStep, scenario: string, crossed: boolean): void {

    // console.log('loadWidgetData',vision, visionStep.level, scenario, crossed);

    let dlo: DataLoadObj = this.getDataloadByKey(vision.code, visionStep.level, scenario);

    // j'ai peut-être chargé ce DLO en mode 'fake' sur un calcul remote.
    // dans ce cas, je dois le supprimer
    if(!!dlo?.remoteComputed) {
      this.resetOne(dlo.visionStep.visionCode, dlo.visionStep.level, dlo.scenario);
      
      // on vérifie le fei
      let fei: FeiVO = this.model.dataEngine.getFei(COMPUTE_MODE.LOCAL, dlo.visionStep.level, dlo.scenario) as FeiVO;
      if(fei) {
        fei.computeMode = COMPUTE_MODE.LOCAL;
        // console.log('fei', fei.uniqueName, fei.computeMode);
      }
      dlo = null;

    }

    // déjà chargé et complet, on peut répondre tout de suite
    if (dlo?.state === DataLoadObj.LOADED) {
      // console.log('loadWidgetData already', visionStep.level, scenario, dlo.revisionNumber);     
      this.model.dataEngine.checkAllDependenciesReady(dlo); // déjà OK, on le signale au dataEngine
      return;
    }

    if (!dlo) { // pas encore inscrit, première demande
      // console.log('loadWidgetData load datas', vision, visionStep.level, scenario);
      dlo = new DataLoadObj(this.getFeiLocalKey(visionStep, scenario), visionStep, scenario);
      dlo.crossed = crossed; // marquer les DLOs liés à d'autres visions
      // inscription dans la map
      this.feedMapDataload(dlo);
      // console.log('feed dlo', dlo.uniqueKey, this.model.dataMap);
      //  this.feedParentScenarii(dataLoad, vision.code, scen);

      // chargement du jeu local
      const cmd: LocalDataCommand = new LocalDataCommand(this.model).getLocal(dlo, null);
      from(cmd.execute())
        .pipe(take(1))
        .subscribe(() => {
          new CountDataCommand(this.model).count(dlo, this.backFromCount.bind(this)).execute();
        });

    } else {
      // console.log('loadWidgetData got it', visionStep.level, scenario);
    }

  }

  /**
   * #### étape 02 : retour de dénombrement remote, lancement des GroupCommands de chargement
   * par level#scenario
   * @param result de la commande de dénombrement
   */
  private backFromCount(result: CountDataCommand): void {
    const dataLoad = result.dataLoad;
    // console.log('WDS count', dataLoad, dataLoad.nbLines, dataLoad.nbValues);

    const calls: WizCommand<any>[] = [];
    const grpCmd: WizGroupCommand = new WizGroupCommand();
    let nbCalls: number;
    this.model.dataMessage.doMessage(DataMessage.LOAD_REMOTE_INIT, dataLoad); // alimentation de la console de chargement
    dataLoad.startTime = new Date().getTime(); // début mesure
    if (dataLoad.nbLines > 0) { //nouvelles baselines

      nbCalls = this.getNumSegments(dataLoad.nbLines);
      for (let n = 0; n < nbCalls; ++n) { // chargement des baselines
        const findDataCmd: FindDataLinesCommand = new FindDataLinesCommand(this.model);
        findDataCmd.find(dataLoad, n, WidgetDataService.CHUNK_SIZE);
        calls.push(findDataCmd);
      }
    }

    if (dataLoad.nbValues > 0) { //nouvelles baselineValues
      nbCalls = this.getNumSegments(dataLoad.nbValues);
      for (let n = 0; n < nbCalls; ++n) { // chargement des baselineValues
        const findDataCmd: FindDataValuesCommand = new FindDataValuesCommand(this.model);
        findDataCmd.find(dataLoad, n, WidgetDataService.CHUNK_SIZE);
        calls.push(findDataCmd);
      }
    }

    // recherche des supprimés, inutile sur init
    if (dataLoad.revisionNumber > 0) {
      calls.push(new DeletedEmsCmd(this.model).findBaseline(dataLoad));
    }

    if (calls.length > 0) { // a minima une commande à jouer
      grpCmd.configure(calls, this.groupCallReturn.bind(this), false);
      grpCmd.carrier = dataLoad;
      grpCmd.execute();
    } else { // rien en remote
      this.allRemoteLoaded(dataLoad);
    }
  }

  /**
   * retour d'appels remote
   * @param grpCmd la pile de findLines / findValues
   */
  private groupCallReturn(grpCmd: WizGroupCommand): void {
    // console.log('allIn before merge', grpCmd.carrier);
    // on alimente le dataload avec les resultSets des commandes
    grpCmd.commandPile.forEach((cmd: WizCommand<any>) => {
      cmd.resolve();
    });

    this.allRemoteLoaded(grpCmd.carrier);
  }


  /**
   * #### étape 03 : tout est chargé pour CE dataload, on persiste en local
   * @param dataLoad 
   */
  private allRemoteLoaded(dataLoad: DataLoadObj) {
    this._merge(dataLoad); // merge local et remote, + persistence locale si nécessaire 
  }

  /** Merge entre données locales et distantes (gestion ajout / update / delete) */
  private _merge(dataLoad: DataLoadObj): void {

    this.mergeBaselines(dataLoad);

    // persistence locale
    if (dataLoad.remoteRevisionNumber > dataLoad.revisionNumber || dataLoad.toPersistBaseline?.size > 0 || dataLoad.toDieBaseline?.size > 0) { // données remote sur init, ou retour de service de sync 
      // console.log('wds before persist', dataLoad.remoteRevisionNumber, dataLoad.revisionNumber, dataLoad.uniqueKey);
      dataLoad.previousRevisionNumber = dataLoad.revisionNumber;
      dataLoad.revisionNumber = dataLoad.remoteRevisionNumber;
      // AllkeyWebWorker.storeFei(dataLoad.uniqueKey, this.dataloadToLocalFei(dataLoad));
      // et ici les Baselines
      if (dataLoad.toPersistBaseline?.size > 0) { // on ajoute avant de passer au check de suppression
        this.addBDDStep(dataLoad);
      } else { // pas d'ajout, directement step de check de suppression
        this.deleteBDDStep(dataLoad);
      }
    } else { // pas de modif, on persiste juste le FEI
      this.finalBDDStep(dataLoad);
    }
  }

  /**
   * step d'ajout des baselines
   * @param dataLoad 
   */
  private addBDDStep(dataLoad: DataLoadObj): void {
    // console.log(dataLoad.uniqueKey, dataLoad.toPersistBaseline.size)
    // console.log('doBulkAddBaselines start', new Date().getTime());
    const pairs: BddBaseline[] = [];
    [...dataLoad.toPersistBaseline.values()].forEach((bl: Baseline) => {
      pairs.push({ id: bl.id, feiKey: dataLoad.uniqueKey, key: bl.key, level: bl.level, scen: bl.scen, value: bl });
    });
    // console.log('doBulkAddBaselines prepared', new Date().getTime());
    AkmBDD.getInstance().doBulkAddBaselines(pairs, dataLoad, this.deleteBDDStep.bind(this));
  }

  /**
   * step de suppression des baselines
   * @param dataLoad 
   */
  private deleteBDDStep(dataLoad: DataLoadObj): void {
    if (dataLoad.toDieBaseline?.size > 0) {
      // console.log(dataLoad.uniqueKey, dataLoad.toDieBaseline.size);
      const pairs: any[] = [];
      [...dataLoad.toDieBaseline.values()].forEach((bl: Baseline) => {
        // console.log('bdd delete', bl.id, bl.scen, bl.key);
        pairs.push(bl.id);
      });
      AkmBDD.getInstance().doBulkDelete(AkmBDD.BDD_BASELINE_KEY, pairs, dataLoad, this.finalBDDStep.bind(this));
    } else {
      this.finalBDDStep(dataLoad);
    }
  }

  /**
   * persistance du FEI à jour avec ses numRev
   * @param dataLoad 
   */
  private finalBDDStep(dataLoad: DataLoadObj): void {
    
    // console.log('finalBDDStep', dataLoad.uniqueKey, dataLoad.merged);
    
    if (!dataLoad.merged) { // on est sur l'init
      // persistence du FEI
      let bddfei: BddFEI = this.dataloadToLocalFei(dataLoad);
      // console.log('dataloadToLocalFei 1', dataLoad.uniqueKey, bddfei);
      AkmBDD.getInstance().doAdd(AkmBDD.BDD_FEI_KEY, dataLoad.uniqueKey, this.dataloadToLocalFei(dataLoad));
      // console.log('dataloadToLocalFei 2', dataLoad.uniqueKey, bddfei);
      dataLoad.merged = true;

      // console.log('allIn after merge', dataLoad);
      dataLoad.endTime = new Date().getTime(); // début mesure
      dataLoad.totalRemoteTime = (dataLoad.endTime - dataLoad.startTime) / 1000;

      if (!dataLoad.remoteComputed) {
        // Les lignes ne sont pas encore calculées. On met à jour le statut pour indiquer qu'elle sont chargées
        this.model.dataMessage.doMessage(DataMessage.LOAD_REMOTE_ENDED, dataLoad);
        dataLoad.state = DataLoadObj.LOADED; // ok, chargement fini pour lui
      }

      if(dataLoad.crossed) {
        this.model.dataMessage.doMessage(DataMessage.ENDED, dataLoad); // pour fermeture éventuelle de la popUp
        this.model.dataEngine.feedCrossedLines(dataLoad);
        // console.log('merge crossed', dataLoad);
      } 
        
      // on signale que ce DLO est prêt. Les FEIs vont contrôler que tout est dispo pour eux...
      // en cas de crossVision, le checkAllDependenciesReady va juste contrôler les dépendances
      this.model.dataEngine.checkAllDependenciesReady(dataLoad);
      


    }
  }

  /**
   * sur retour de commit composite, mise à jour du cache et des dataload chargés
   * @param datapack 
   * @returns les modifications identifiées sur les filterKeys Map<Baseline> = Set<codes filter modifiées>
   */
  public eatRemote(datapack: DataPack): Map<Baseline, Set<string>> {
    let filterKeyUpdates: Map<Baseline, Set<string>> = null;

    if (!!datapack.datas) {
      Object.keys(datapack.datas).forEach((lvlStr: string) => {
        Object.keys(datapack.datas[lvlStr]).forEach((scen: string) => {
          
          let datas: DataPackSet = datapack.datas[lvlStr][scen];
          let dlo: DataLoadObj = this.getDataloadByKey(datapack.visionCode, datas.level, datas.scenario);

          if(dlo.remoteRevisionNumber === datas.revNumber) { // ici on est sur un event socket suite à mon propre commit
            // du coup on ne fait rien
            // console.log('self socket event');
          } else {

            this.resetDataload(dlo); // préparation pour le merge

            // prise du numrev
            dlo.remoteRevisionNumber = datas.revNumber;

            let blite: BaselineLite;
            let blvlite: BaselineValueLite;

            // gestion des suppressions en premier, en cas de delete / recreate
            datas.deleted?.forEach((emt: EntityModificationTrace) => {
              if (emt.clas === BASIC_ENTITY_TYPE.BASELINE) {
                dlo.deletedBaselines.set(emt.key, emt);
              } else if (emt.clas === BASIC_ENTITY_TYPE.BASELINE_VALUE) {
                if (!dlo.deletedBaselineValues.has(emt.key)) {
                  dlo.deletedBaselineValues.set(emt.key, []);
                }
                dlo.deletedBaselineValues.get(emt.key).push(emt);
              }

            });

            // gestion des objets remote
            datas.remoteBaselines?.forEach((bl: BaselineLite) => {

              // une vraie comparaison
              let exist: Baseline = dlo.localBaselines[bl.key];
              if (!!exist) {
                let delta: Set<string> = BaselineEngine.checkFilterKeyDelta(exist.fkeyjson, bl.fkey || {})
                if (delta.size > 0) {
                  if (!filterKeyUpdates) {
                    filterKeyUpdates = new Map();
                  }
                  filterKeyUpdates.set(exist, delta);
                  // console.log('FilterKeyDelta on remote', exist, bl, delta);
                }
              }

              dlo.remoteBaselines[bl.key] = bl; // y'a pas de values sur une lite, on a fini
            });

            // gestion des BLs
            datas.baselines?.forEach((bl: Baseline) => {
              if (bl.operation === EM_ACTION.CREATE || bl.operation === EM_ACTION.UPDATE) {
                blite = BaselineEngine.baselineToBaselineLite(bl);


                // TODO faire une vraie comparaison
                let exist: Baseline = dlo.localBaselines[blite.key];
                if (exist) {
                  let delta: Set<string> = BaselineEngine.checkFilterKeyDelta(exist.fkeyjson, bl.fkeyjson || {})
                  if (delta.size > 0) {
                    if (!filterKeyUpdates) {
                      filterKeyUpdates = new Map();
                    }
                    // if(!filterKeyUpdates) {
                    //   filterKeyUpdates = new Map();
                    // }
                    filterKeyUpdates.set(exist, delta);
                    // console.log('FilterKeyDelta on baselines (mines)', exist, bl, delta);
                  }
                }

                dlo.remoteBaselines[blite.key] = blite;
                if (bl.operation === EM_ACTION.CREATE) {
                  if (!!bl.values) { // persistence des baselineValues
                    [...Object.values(bl.values)].forEach((blv: BaselineValue) => {
                      blv.operation = EM_ACTION.CREATE; // on garde l'opération sur la BLV pour cycle de refresh
                      dlo.remoteBaselineValues.push(BaselineEngine.baselineValueToBaselineValueLite(blv));
                    });
                  }
                }
              };
            });

            // gestion des objets remote
            datas.remoteBaselineValues?.forEach((blv: BaselineValueLite) => {
              dlo.remoteBaselineValues.push(blv);
            });

            // gestion des BLVs
            datas.baselineValues?.forEach((blv: BaselineValue) => {
              if (blv.operation === EM_ACTION.CREATE || blv.operation === EM_ACTION.UPDATE) {
                blvlite = BaselineEngine.baselineValueToBaselineValueLite(blv);
                dlo.remoteBaselineValues.push(blvlite);
              }
            });
            // TODO TODO vérifier... c'est asynchrone... mais PAS la suite du traitement back commit...
            this._merge(dlo); // merge du pool et persistence locale 
          }
        });
      });
    }

    return filterKeyUpdates;
  }

}
